import type { CompositionSettings, ExportPreset, Project } from '@cinegrid/core';
import { EXPORT_PRESETS } from '@cinegrid/core';
import { supportsMediaRecorder, supportsWebCodecs } from './importMedia';
import {
  mixTimelineAudio,
  mixedToAudioBuffer,
  type MixedAudio,
} from './mixTimelineAudio';

export interface ExportProgress {
  status: 'preparing' | 'rendering' | 'muxing' | 'done' | 'error' | 'cancelled';
  progress: number;
  method?: 'webcodecs' | 'mediarecorder';
  error?: string;
  blob?: Blob;
}

export type ProgressCb = (p: ExportProgress) => void;

function resolvePreset(preset: ExportPreset, settings: CompositionSettings): ExportPreset {
  if (preset.id === 'match-timeline' || preset.width === 0) {
    return {
      ...preset,
      width: settings.width,
      height: settings.height,
      frameRate: settings.frameRate,
    };
  }
  return preset;
}

/**
 * Export the composition by capturing frames from a provided render callback.
 * Primary: WebCodecs + mp4-muxer (H.264 + AAC when audio is present)
 * Fallback: MediaRecorder on a canvas + audio MediaStream
 *
 * When rangeStart/rangeEnd are provided, only that In→Out span is exported.
 */
export async function exportComposition(opts: {
  project: Project;
  presetId: string;
  /** Absolute timeline time of first exported frame */
  rangeStart?: number;
  /** Absolute timeline time of end (exclusive-ish; last frame before this) */
  rangeEnd?: number;
  /** Renders one frame at absolute timeline time t into the canvas. */
  renderFrame: (time: number, canvas: HTMLCanvasElement) => Promise<void> | void;
  canvas: HTMLCanvasElement;
  signal?: AbortSignal;
  onProgress?: ProgressCb;
}): Promise<Blob> {
  const preset = resolvePreset(
    EXPORT_PRESETS.find((p) => p.id === opts.presetId) ?? EXPORT_PRESETS[0],
    opts.project.settings
  );

  const fullDuration = Math.max(
    opts.project.settings.duration,
    ...opts.project.clips.map((c) => c.start + c.duration),
    0.1
  );

  let start = opts.rangeStart ?? 0;
  let end = opts.rangeEnd ?? fullDuration;
  if (end <= start) {
    end = start + 1 / (preset.frameRate || 30);
  }
  start = Math.max(0, start);
  end = Math.min(fullDuration, end);
  const duration = Math.max(end - start, 1 / (preset.frameRate || 30));

  opts.canvas.width = preset.width;
  opts.canvas.height = preset.height;

  opts.onProgress?.({ status: 'preparing', progress: 0 });

  const mixedAudio = await mixTimelineAudio(opts.project, start, end, 44_100);
  if (opts.signal?.aborted) throw new Error('cancelled');

  const renderOpts = {
    ...opts,
    preset,
    duration,
    rangeStart: start,
    mixedAudio,
  };

  if (supportsWebCodecs() && preset.container === 'mp4') {
    try {
      return await exportWithWebCodecs(renderOpts);
    } catch (err) {
      console.warn('WebCodecs export failed, falling back to MediaRecorder', err);
    }
  }

  if (supportsMediaRecorder()) {
    return exportWithMediaRecorder(renderOpts);
  }

  throw new Error('No export path available in this browser');
}

async function resolveAacOrOpus(
  sampleRate: number,
  bitrate: number
): Promise<{ encoderCodec: string; muxCodec: 'aac' | 'opus' } | null> {
  if (typeof AudioEncoder === 'undefined') return null;
  const candidates: Array<{ encoderCodec: string; muxCodec: 'aac' | 'opus' }> = [
    { encoderCodec: 'mp4a.40.2', muxCodec: 'aac' },
    { encoderCodec: 'opus', muxCodec: 'opus' },
  ];
  for (const c of candidates) {
    try {
      const support = await AudioEncoder.isConfigSupported({
        codec: c.encoderCodec,
        numberOfChannels: 2,
        sampleRate,
        bitrate,
      });
      if (support.supported) return c;
    } catch {
      /* try next */
    }
  }
  return null;
}

async function encodeMixedAudio(
  mixed: MixedAudio,
  bitrate: number,
  encoderCodec: string,
  onChunk: (chunk: EncodedAudioChunk, meta?: EncodedAudioChunkMetadata) => void,
  signal?: AbortSignal
): Promise<void> {
  if (typeof AudioEncoder === 'undefined' || typeof AudioData === 'undefined') {
    throw new Error('AudioEncoder unavailable');
  }

  const encoder = new AudioEncoder({
    output: (chunk, meta) => onChunk(chunk, meta),
    error: (e) => console.error('AudioEncoder', e),
  });

  encoder.configure({
    codec: encoderCodec,
    numberOfChannels: 2,
    sampleRate: mixed.sampleRate,
    bitrate,
  });

  const frameSize = 1024;
  const [left, right] = mixed.channels;

  for (let offset = 0; offset < mixed.length; offset += frameSize) {
    if (signal?.aborted) {
      encoder.close();
      throw new Error('cancelled');
    }
    const frames = Math.min(frameSize, mixed.length - offset);
    const planar = new Float32Array(frames * 2);
    planar.set(left.subarray(offset, offset + frames), 0);
    planar.set(right.subarray(offset, offset + frames), frames);

    const data = new AudioData({
      format: 'f32-planar',
      sampleRate: mixed.sampleRate,
      numberOfFrames: frames,
      numberOfChannels: 2,
      timestamp: Math.round((offset / mixed.sampleRate) * 1_000_000),
      data: planar,
    });
    encoder.encode(data);
    data.close();
  }

  await encoder.flush();
  encoder.close();
}

async function exportWithWebCodecs(opts: {
  project: Project;
  preset: ExportPreset;
  duration: number;
  rangeStart: number;
  mixedAudio: MixedAudio | null;
  renderFrame: (time: number, canvas: HTMLCanvasElement) => Promise<void> | void;
  canvas: HTMLCanvasElement;
  signal?: AbortSignal;
  onProgress?: ProgressCb;
}): Promise<Blob> {
  const { Muxer, ArrayBufferTarget } = await import('mp4-muxer');
  const fps = opts.preset.frameRate || 30;
  const totalFrames = Math.max(1, Math.ceil(opts.duration * fps));
  const target = new ArrayBufferTarget();
  const audioBitrate = opts.preset.audioBitrate || 192_000;

  const audioCodec =
    opts.mixedAudio != null
      ? await resolveAacOrOpus(opts.mixedAudio.sampleRate, audioBitrate)
      : null;

  const muxer = new Muxer({
    target,
    video: {
      codec: 'avc',
      width: opts.preset.width,
      height: opts.preset.height,
      frameRate: fps,
    },
    audio: audioCodec
      ? {
          codec: audioCodec.muxCodec,
          numberOfChannels: 2,
          sampleRate: opts.mixedAudio!.sampleRate,
        }
      : undefined,
    fastStart: 'in-memory',
  });

  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => console.error(e),
  });

  encoder.configure({
    codec: 'avc1.42001f',
    width: opts.preset.width,
    height: opts.preset.height,
    bitrate: opts.preset.bitrate,
    framerate: fps,
  });

  opts.onProgress?.({ status: 'rendering', progress: 0, method: 'webcodecs' });

  for (let i = 0; i < totalFrames; i++) {
    if (opts.signal?.aborted) {
      encoder.close();
      throw new Error('cancelled');
    }
    const time = opts.rangeStart + i / fps;
    await opts.renderFrame(time, opts.canvas);
    const frame = new VideoFrame(opts.canvas, {
      timestamp: (i * 1_000_000) / fps,
      duration: 1_000_000 / fps,
    });
    encoder.encode(frame, { keyFrame: i % (fps * 2) === 0 });
    frame.close();
    if (i % 5 === 0) {
      opts.onProgress?.({
        status: 'rendering',
        progress: (i / totalFrames) * 0.85,
        method: 'webcodecs',
      });
      await yieldToMain();
    }
  }

  await encoder.flush();
  encoder.close();

  if (opts.mixedAudio && audioCodec) {
    opts.onProgress?.({ status: 'muxing', progress: 0.88, method: 'webcodecs' });
    try {
      await encodeMixedAudio(
        opts.mixedAudio,
        audioBitrate,
        audioCodec.encoderCodec,
        (chunk, meta) => muxer.addAudioChunk(chunk, meta),
        opts.signal
      );
    } catch (err) {
      if (err instanceof Error && err.message === 'cancelled') throw err;
      console.warn('Audio encode failed; exporting without usable audio', err);
    }
  }

  opts.onProgress?.({ status: 'muxing', progress: 0.95, method: 'webcodecs' });
  muxer.finalize();
  const blob = new Blob([target.buffer], { type: 'video/mp4' });
  opts.onProgress?.({ status: 'done', progress: 1, method: 'webcodecs', blob });
  return blob;
}

async function exportWithMediaRecorder(opts: {
  project: Project;
  preset: ExportPreset;
  duration: number;
  rangeStart: number;
  mixedAudio: MixedAudio | null;
  renderFrame: (time: number, canvas: HTMLCanvasElement) => Promise<void> | void;
  canvas: HTMLCanvasElement;
  signal?: AbortSignal;
  onProgress?: ProgressCb;
}): Promise<Blob> {
  const fps = opts.preset.frameRate || 30;

  const withAudio = Boolean(opts.mixedAudio);
  const mimeCandidates = withAudio
    ? [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9',
        'video/webm',
      ]
    : [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
      ];
  const mime = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) ?? '';
  if (!mime) throw new Error('MediaRecorder WebM not supported');

  const canvasStream = opts.canvas.captureStream(fps);
  const tracks: MediaStreamTrack[] = [...canvasStream.getVideoTracks()];

  let audioCtx: AudioContext | null = null;
  let bufferSource: AudioBufferSourceNode | null = null;

  if (opts.mixedAudio) {
    try {
      audioCtx = new AudioContext({ sampleRate: opts.mixedAudio.sampleRate });
      const dest = audioCtx.createMediaStreamDestination();
      const buf = mixedToAudioBuffer(opts.mixedAudio);
      bufferSource = audioCtx.createBufferSource();
      bufferSource.buffer = buf;
      bufferSource.connect(dest);
      tracks.push(...dest.stream.getAudioTracks());
    } catch (err) {
      console.warn('MediaRecorder audio setup failed', err);
    }
  }

  const stream = new MediaStream(tracks);
  const recorder = new MediaRecorder(stream, {
    mimeType: mime,
    videoBitsPerSecond: opts.preset.bitrate,
    audioBitsPerSecond: opts.preset.audioBitrate || 192_000,
  });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data);
  };

  opts.onProgress?.({ status: 'rendering', progress: 0, method: 'mediarecorder' });

  const done = new Promise<Blob>((resolve, reject) => {
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mime });
      opts.onProgress?.({ status: 'done', progress: 1, method: 'mediarecorder', blob });
      resolve(blob);
    };
    recorder.onerror = () => reject(new Error('MediaRecorder failed'));
  });

  if (audioCtx?.state === 'suspended') {
    await audioCtx.resume().catch(() => undefined);
  }

  recorder.start(100);
  bufferSource?.start(0);

  const totalFrames = Math.max(1, Math.ceil(opts.duration * fps));
  const frameBudgetMs = 1000 / fps;
  for (let i = 0; i < totalFrames; i++) {
    if (opts.signal?.aborted) {
      bufferSource?.stop();
      recorder.stop();
      await audioCtx?.close().catch(() => undefined);
      throw new Error('cancelled');
    }
    const t0 = performance.now();
    await opts.renderFrame(opts.rangeStart + i / fps, opts.canvas);
    opts.onProgress?.({
      status: 'rendering',
      progress: i / totalFrames,
      method: 'mediarecorder',
    });
    const spent = performance.now() - t0;
    const wait = Math.max(0, frameBudgetMs - spent);
    if (wait > 0) await sleep(wait);
  }

  bufferSource?.stop();
  recorder.stop();
  await audioCtx?.close().catch(() => undefined);
  return done;
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function yieldToMain() {
  return new Promise<void>((r) => setTimeout(r, 0));
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
